import { AggregateCartesianChartDataConfig, BasicChartDataSource } from '@app/data/interface/widget-config';
import { SeriesOption } from 'echarts';
import { WidgetNumberUtils } from './widget-number-utils';

interface LizardSurveySourceDefinition {
  sourceIdentifier: string;
  label: string;
}

interface LizardTooltipEntry {
  vegetation: string[];
  species: Array<{ name: string; value: number }>;
}

interface LizardSourceAggregation {
  identifier: string;
  label: string;
  speciesTotals: Map<string, number>;
  vegetation: Set<string>;
}

/**
 * Definitions of lizard survey data sources.
 */
export const lizardSurveySources: ReadonlyArray<LizardSurveySourceDefinition> = [
  { sourceIdentifier: 'arl_pc1', label: 'PC1' },
  { sourceIdentifier: 'arl_pe2', label: 'PE2' },
  { sourceIdentifier: 'arl_pe4', label: 'PE4' },
  { sourceIdentifier: 'arl_pe6', label: 'PE6' },
];

const allowedSourceIdentifiers = new Set<string>(lizardSurveySources.map((entry) => entry.sourceIdentifier));
let tooltipMeta: Record<string, LizardTooltipEntry> = {};

/**
 * Creates an empty aggregation object for a lizard survey source.
 * @param identifier The unique identifier for the source.
 * @param label The human-readable label for the source.
 * @returns A new LizardSourceAggregation object.
 */
const createEmptyAggregation = (identifier: string, label: string): LizardSourceAggregation => ({
  identifier,
  label,
  speciesTotals: new Map<string, number>(),
  vegetation: new Set<string>(),
});

/**
 * Extracts unique vegetation values from the provided metadata array.
 * @param metadata The metadata array to extract vegetation values from.
 * @returns An array of unique vegetation values.
 */
const extractVegetationValues = (metadata: unknown): string[] => {
  if (!Array.isArray(metadata)) return [];
  const seen = new Set<string>();
  const values: string[] = [];

  metadata.forEach((entry) => {
    if (!entry || typeof (entry as any).Vegetation !== 'string') return;
    const trimmed = (entry as any).Vegetation.trim();
    if (!trimmed || seen.has(trimmed)) return;
    seen.add(trimmed);
    values.push(trimmed);
  });

  return values;
};

/**
 * Registers an entity name in the lookup map.
 * @param lookup The lookup map to register the entity name in.
 * @param entity The entity to register.
 * @returns The registered entity name or undefined if registration failed.
 */
const registerEntityName = (lookup: Map<string, string>, entity: any) => {
  if (!entity || entity.id == null) return;
  if (typeof entity.name !== 'string') return;
  const trimmed = entity.name.trim();
  if (!trimmed) return;
  lookup.set(String(entity.id), trimmed);
};

/**
 * Appends entity names to the lookup map.
 * @param lookup The lookup map to append entity names to.
 * @param entities The entities to register.
 * @returns The number of entities registered.
 */
const appendEntities = (lookup: Map<string, string>, entities: any) => {
  if (!Array.isArray(entities)) return;
  entities.forEach((entity: any) => registerEntityName(lookup, entity));
};
/**
 * Processes an environmental source and updates the relevant mappings.
 * @param source The environmental source to process.
 * @param allowedIdentifiers The set of allowed source identifiers.
 * @param idToLabelMap The map of ID to label mappings.
 * @param aggregations The map of source aggregations.
 * @param sourceIdToIdentifier The map of source IDs to identifiers.
 * @returns
 */
const processEnvironmentalSource = (
  source: any,
  allowedIdentifiers: Set<string>,
  idToLabelMap: Record<string, string>,
  aggregations: Map<string, LizardSourceAggregation>,
  sourceIdToIdentifier: Map<string, string>
) => {
  if (!source) return;
  const identifier = typeof source.source_identifier === 'string' ? source.source_identifier : undefined;
  if (!identifier || !allowedIdentifiers.has(identifier)) return;

  const sourceId = source.id != null ? String(source.id) : identifier;
  if (sourceId) sourceIdToIdentifier.set(sourceId, identifier);

  const aggregation = aggregations.get(identifier);
  if (!aggregation) return;

  const rawLabel = typeof source.human_readable_name === 'string' ? source.human_readable_name.trim() : '';
  if (rawLabel) {
    aggregation.label = rawLabel;
    return;
  }

  if (sourceId && idToLabelMap[sourceId]) {
    aggregation.label = idToLabelMap[sourceId];
  }
};

/**
 * Builds a lookup map of entity IDs to their names from the provided series data.
 * @param seriesData The series data to extract entity names from.
 * @returns A map of entity IDs to their names.
 */
const buildEntityLookup = (seriesData: any): Map<string, string> => {
  const lookup = new Map<string, string>();

  appendEntities(lookup, seriesData?.entities);
  appendEntities(lookup, seriesData?.observationEntities);

  const sources: any[] = Array.isArray(seriesData?.environmentalDataSources) ? seriesData.environmentalDataSources : [];

  sources.forEach((source: any) => {
    appendEntities(lookup, source?.entities);
    appendEntities(lookup, source?.observationEntities);
  });

  return lookup;
};

/**
 * Process a single data row (point) and apply it to aggregations.
 * Extracted to module scope to reduce closure complexity inside the mapper.
 */
const processPoint = (
  row: any,
  allowedSourceIdentifiers: Set<string>,
  sourceIdToIdentifier: Map<string, string>,
  aggregations: Map<string, LizardSourceAggregation>,
  dataSource: any,
  entityLookup: Map<string, string>,
  ensureSpeciesRegistered: (speciesName: string) => void
): void => {
  if (!row) return;

  const sourceKey = String(row.environmental_data_source_id);
  let identifier = sourceIdToIdentifier.get(sourceKey);
  if (!identifier && allowedSourceIdentifiers.has(sourceKey)) {
    identifier = sourceKey;
  }
  if (!identifier || !allowedSourceIdentifiers.has(identifier)) return;

  const aggregation = aggregations.get(identifier);
  if (!aggregation) return;

  const valueField = dataSource?.valueField ?? 'sum';
  const rawValue = row.hasOwnProperty(valueField) ? row[valueField] : row.sum ?? row.avg ?? row.count;
  const numericValue = WidgetNumberUtils.toFiniteNumber(rawValue) ?? 0;
  const observationId = row.observation_entity_id ?? row.observationEntityId ?? row.entity_id;
  const speciesName = observationId != null ? entityLookup.get(String(observationId)) ?? String(observationId) : null;

  if (speciesName && numericValue > 0) {
    ensureSpeciesRegistered(speciesName);
    aggregation.speciesTotals.set(speciesName, (aggregation.speciesTotals.get(speciesName) ?? 0) + numericValue);
  }

  const vegetationValues = extractVegetationValues(row.collection_metadata);
  vegetationValues.forEach((name) => aggregation.vegetation.add(name));
};

/**
 * Build tooltip metadata mapping from axis labels to vegetation and species breakdowns.
 */
const buildTooltipMeta = (
  axisLabels: string[],
  sourceOrder: string[],
  aggregations: Map<string, LizardSourceAggregation>,
  speciesOrder: string[]
): Record<string, LizardTooltipEntry> => {
  const meta: Record<string, LizardTooltipEntry> = {};

  axisLabels.forEach((label, index) => {
    const identifier = sourceOrder[index];
    const aggregation = aggregations.get(identifier);
    if (!aggregation) return;

    meta[label] = {
      vegetation: Array.from(aggregation.vegetation),
      species: speciesOrder.map((species) => ({
        name: species,
        value: aggregation.speciesTotals.get(species) ?? 0,
      })),
    };
  });

  return meta;
};

/**
 * Resets the tooltip metadata.
 */
const resetTooltipMeta = () => {
  tooltipMeta = {};
};

/**
 * Maps the Lizard survey data to the appropriate series format.
 *
 * If the caller provides a list of selected data source objects, only those sources
 * will be included in the mapping. If the special identifier 'all' is included in
 * the selection, all known lizard survey sources will be used.
 *
 * If the selection is empty or results in no allowed sources, an empty series array
 * will be returned to allow the caller to display a no-data state.
 *
 * If there are no selected/allowed identifiers, return empty series so the caller can
 * display a no-data state.
 *
 * @param _keyedBySourceId The keyed by source ID map.
 * @param idToLabelMap The ID to label map.
 * @param xAxisData The X-axis data array.
 * @param legendLabels The legend labels array.
 * @param dataSource The data source configuration.
 * @param seriesData The series data to map.
 * @returns The mapped series data.
 */
export const mapLizardSurveyData: AggregateCartesianChartDataConfig['mapDataToSeries'] = (
  _keyedBySourceId,
  idToLabelMap,
  xAxisData,
  legendLabels,
  dataSource,
  seriesData,
  selectedSources?: BasicChartDataSource[]
) => {
  resetTooltipMeta();

  let allowedSet =
    Array.isArray(selectedSources) && selectedSources.length > 0
      ? new Set(
          selectedSources
            .map((s) => s && (s.environmentalDataSourceIdentifier ?? (s as any).environmentalDataSourceIdentifier))
            .filter(Boolean) as string[]
        )
      : new Set<string>(lizardSurveySources.map((entry) => entry.sourceIdentifier));

  if (allowedSet.has('all')) {
    allowedSet = new Set<string>(lizardSurveySources.map((entry) => entry.sourceIdentifier));
  }

  const sourceOrder = lizardSurveySources
    .map((entry) => entry.sourceIdentifier)
    .filter((identifier) => allowedSet.has(identifier));

  const aggregations = new Map<string, LizardSourceAggregation>();
  lizardSurveySources.forEach((entry) => {
    aggregations.set(entry.sourceIdentifier, createEmptyAggregation(entry.sourceIdentifier, entry.label));
  });

  const entityLookup = buildEntityLookup(seriesData);
  const sourceIdToIdentifier = new Map<string, string>();

  const environmentalSources: any[] = Array.isArray(seriesData?.environmentalDataSources)
    ? seriesData.environmentalDataSources
    : [];

  environmentalSources.forEach((source: any) =>
    processEnvironmentalSource(source, allowedSourceIdentifiers, idToLabelMap, aggregations, sourceIdToIdentifier)
  );

  const speciesOrder: string[] = [];
  const ensureSpeciesRegistered = (speciesName: string) => {
    if (!speciesOrder.includes(speciesName)) speciesName && speciesOrder.push(speciesName);
  };

  const points = Array.isArray(seriesData?.data) ? seriesData.data : [];
  points.forEach((row: any) =>
    processPoint(
      row,
      allowedSourceIdentifiers,
      sourceIdToIdentifier,
      aggregations,
      dataSource,
      entityLookup,
      ensureSpeciesRegistered
    )
  );

  const axisLabels = sourceOrder.map((identifier) => aggregations.get(identifier)?.label ?? identifier);

  if (sourceOrder.length === 0) {
    xAxisData.length = 0;
    legendLabels.length = 0;
    return [] as SeriesOption[];
  }

  xAxisData.length = 0;
  xAxisData.push(...axisLabels);

  legendLabels.length = 0;
  speciesOrder.forEach((species) => legendLabels.push(species));

  const seriesList: SeriesOption[] = speciesOrder.map((species) => {
    const data = sourceOrder.map((identifier) => aggregations.get(identifier)?.speciesTotals.get(species) ?? 0);
    return {
      name: species,
      type: 'bar',
      stack: 'species',
      emphasis: { focus: 'series' },
      barMaxWidth: 48,
      data,
    } as SeriesOption;
  });

  const meta = buildTooltipMeta(axisLabels, sourceOrder, aggregations, speciesOrder);

  tooltipMeta = meta;

  return seriesList;
};

export const lizardSurveyTooltipFormatter = (params: any) => {
  if (!Array.isArray(params) || params.length === 0) return '';

  const axisValue = params[0]?.axisValue ?? '';
  const meta = tooltipMeta[axisValue];
  const lines: string[] = [];

  if (axisValue) {
    lines.push(`<strong>${axisValue}</strong>`);
  }

  if (meta?.vegetation && meta.vegetation.length) {
    lines.push(`<span><strong>Habitat Type:</strong> ${meta.vegetation.join(', ')}</span>`);
  }

  const markerBySeriesName = new Map<string, string>();
  params.forEach((entry: any) => {
    if (entry?.seriesName) {
      markerBySeriesName.set(entry.seriesName, entry.marker);
    }
  });

  const speciesEntries = meta?.species?.length
    ? meta.species
    : params.map((entry: any) => ({ name: entry.seriesName, value: entry.value }));

  speciesEntries.forEach((entry) => {
    if (!entry?.name) return;
    const marker = markerBySeriesName.get(entry.name) ?? '•';
    const value = WidgetNumberUtils.toFiniteNumber(entry.value) ?? 0;
    lines.push(`${marker} ${entry.name}: ${value}`);
  });

  return lines.join('<br/>');
};
